import React, { useCallback, useEffect, useMemo, useState } from "react";
import { shuffle, pick, precisionOf, starsOf, useCountdown, TimerBar, Hud, Flash, PRACTICE_SECONDS, FLASH_MS } from "./nqShared";

const SETS = {
  1: { target: "🦊", distractors: ["🐸", "🐻", "🐰", "🦉"], grid: 20, targets: 5, seconds: 45, shuffleEvery: 0 },
  2: { target: "🟦", distractors: ["🔵", "🟪", "🟩", "🔷"], grid: 30, targets: 6, seconds: 60, shuffleEvery: 0 },
  3: { target: "⭐", distractors: ["🌟", "✨", "💫", "🌠"], grid: 42, targets: 7, seconds: 75, shuffleEvery: 5, portal: { target: "🌙", distractors: ["⭐", "🌟", "☁️", "🌞"] } },
};
const cfgFor = (level, practice) => (practice ? { ...SETS[level.n], seconds: PRACTICE_SECONDS, portal: null, shuffleEvery: 0 } : SETS[level.n]);

export const brief = (level) => {
  const s = SETS[level.n];
  return {
    title: `Trouve les ${s.target} cachés dans la forêt`,
    goal: `trouver les ${s.targets} ${s.target} le plus vite possible.`,
    regarde: `Regarde toute la grille : les ${s.target} se cachent parmi d'autres dessins qui leur ressemblent.`,
    fais: `Touche seulement les ${s.target}. Chaque bonne trouvaille devient ✅.`,
    attention: `Les autres dessins sont des pièges : si tu les touches, tu perds des points.${s.shuffleEvery ? " Au niveau Maître, les dessins bougent et un portail change ta cible : lis bien l'annonce !" : ""}`,
    preview: { ok: [s.target], ko: s.distractors },
  };
};

const build = (cfg) => {
  const cells = [];
  for (let i = 0; i < cfg.targets; i++) cells.push({ id: `t${i}`, emoji: cfg.target, target: true, found: false });
  for (let i = cells.length; i < cfg.grid; i++) cells.push({ id: `d${i}`, emoji: pick(cfg.distractors), target: false, found: false });
  return shuffle(cells);
};

export default function GameLaser({ power, level, onFinish, practice, onPracticeEnd }) {
  const base = useMemo(() => cfgFor(level, practice), [level, practice]);
  const [phase, setPhase] = useState("play");
  const [cfg, setCfg] = useState(base);
  const [cells, setCells] = useState(() => build(base));
  const [errors, setErrors] = useState(0);
  const [hitsTotal, setHitsTotal] = useState(0);
  const [portalUsed, setPortalUsed] = useState(false);
  const [flash, setFlash] = useState(null);
  const running = phase === "play";
  const totalTargets = base.targets + (base.portal ? base.portal.targets || base.targets : 0);

  const finish = useCallback(() => {
    if (practice) return onPracticeEnd();
    const hits = hitsTotal + cells.filter((c) => c.target && c.found).length;
    const precision = precisionOf(hits, totalTargets, errors);
    return onFinish({ hits, total: totalTargets, errors, precision, stars: starsOf(precision), detail: `${hits}/${totalTargets} cibles trouvées · ${errors} piège(s) touché(s)` });
  }, [hitsTotal, cells, totalTargets, errors, onFinish, practice, onPracticeEnd]);

  const left = useCountdown(base.seconds, running, finish);
  const found = cells.filter((c) => c.target && c.found).length;

  useEffect(() => {
    if (!running || !cfg.shuffleEvery) return undefined;
    const id = setInterval(() => setCells((cs) => shuffle(cs)), cfg.shuffleEvery * 1000);
    return () => clearInterval(id);
  }, [running, cfg.shuffleEvery]);

  useEffect(() => {
    if (running && found === cfg.targets) {
      if (base.portal && !portalUsed) {
        const next = { ...base, ...base.portal };
        setHitsTotal((h) => h + found); setPortalUsed(true); setCfg(next); setCells(build(next)); setPhase("portal");
        setTimeout(() => setPhase("play"), 2200);
      } else finish();
    }
  }, [found, running, cfg.targets, base, portalUsed, finish]);

  const click = (cell) => {
    if (!running || cell.found) return;
    if (cell.target) { setCells((cs) => cs.map((c) => (c.id === cell.id ? { ...c, found: true } : c))); setFlash("ok"); }
    else { setErrors((e) => e + 1); setFlash("ko"); }
    setTimeout(() => setFlash(null), FLASH_MS);
  };

  const cols = useMemo(() => (cfg.grid <= 20 ? "grid-cols-5" : cfg.grid <= 30 ? "grid-cols-6" : "grid-cols-7"), [cfg.grid]);

  return (
    <div className="space-y-4" data-testid="nq-game-laser">
      <TimerBar left={left} total={base.seconds} color={power.color} />
      <Hud items={[["🎯 Cible :", cfg.target, power.color], ["Trouvées", `${found}/${cfg.targets}`, "#7CC6A6"], ["Pièges touchés", errors, "#E0705C"]]} />
      <div className="relative">
        {phase === "portal" && (
          <div className="absolute inset-0 z-10 rounded-3xl bg-[#ECE6F7]/95 flex flex-col items-center justify-center text-center p-6 animate-fade-up" data-testid="nq-portal">
            <div className="text-5xl mb-2">🌀</div>
            <div className="font-serif-title text-2xl text-[#5E4A9C]">Le portail des couleurs !</div>
            <p className="text-[var(--ink)] mt-1">Nouvelle cible : <span className="text-3xl align-middle">{cfg.target}</span> — {cfg.targets} à trouver.</p>
          </div>
        )}
        <Flash kind={flash} />
        <div className={`grid ${cols} gap-2 sm:gap-3 rounded-3xl bg-[#E4F5EC] p-3 sm:p-4`}>
          {cells.map((c) => (
            <button key={c.id} onClick={() => click(c)} data-testid={`nq-cell-${c.id}`} aria-label={c.target ? "cible" : "piège"}
              className={`aspect-square rounded-2xl flex items-center justify-center text-2xl sm:text-3xl transition-all ${c.found ? "bg-[#7CC6A6] scale-90 opacity-60" : "bg-white hover:scale-105 active:scale-95 shadow-sm"}`}>
              {c.found ? "✅" : c.emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
